import React from 'react';
import { Dropdown } from '@insurancemenu/imenu-components-lib';
import { DropdownOptionType, OptionsAlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types/Dropdown';
import {
  Edit,
  AssignmentInd,
  ChevronDown,
  Archive,
  Unarchive,
  DriveFile,
  Delete,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import { useRootStore } from 'store/rootStore';
import { GroupFilter } from 'types/groupService';
import { observer } from 'mobx-react-lite';
import { adminRights, imCelticsRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { DASHBOARD_ACTIONS_DROPDOWN_TEST, REMOVE_GROUP_ACTION_BTN_TEST } from 'components/groups-dashboard/constants';
import { DashboardGroupDelegate } from 'store/pages/groups-dashboard/dashboardGroupDelegate';
import { useGroupDashboardPageStore } from 'store/pages/groups-dashboard/pageStore';
import { unarchiveGroup } from 'api/operations/groupService';
import { DropdownButtonStyled } from './styles';

interface Props {
  groupDelegate: DashboardGroupDelegate;
}

const MoreDropdown: React.FC<Props> = observer(
  ({ groupDelegate: { data: groupData, onGroupClick, archiveGroup, removeGroup } }) => {
    const { groupsDashboardController, userStore, domainStoreV2 } = useRootStore();

    const canEditAssignedInfo =
      !domainStoreV2.adminOnlyAgentAssignment ||
      (domainStoreV2.adminOnlyAgentAssignment && adminRights(userStore.userRoles));
    const { groupRequestParams } = useGroupDashboardPageStore();

    const isImCeltics = imCelticsRights(userStore.userRoles);

    function onAssignAgents(): void {
      groupsDashboardController.openAssignAgentsModal();
      void groupsDashboardController.uploadGroupToGroupStore(groupData.id);
    }

    const activeGroupDropdownOptions: DropdownOptionType[] = [
      {
        value: 'editGroup',
        onOptionClick: onGroupClick,
        icon: Edit,
        label: 'Edit Group',
      },
      ...(canEditAssignedInfo
        ? [
            {
              value: 'assignAgents',
              onOptionClick: onAssignAgents,
              label: 'Assign Agents',
              icon: AssignmentInd,
            },
          ]
        : []),
      ...(!domainStoreV2.imqSponsor
        ? [
            {
              value: 'archiveGroup',
              onOptionClick: archiveGroup,
              icon: Archive,
              label: 'Archive Group',
            },
          ]
        : []),
    ];

    const archivedGroupDropdownOption: DropdownOptionType[] = [
      {
        value: 'editGroup',
        onOptionClick: onGroupClick,
        label: 'View Group',
        icon: DriveFile,
      },
      {
        value: 'unarchiveGroup',
        onOptionClick: async (): Promise<void> => {
          await unarchiveGroup(groupData.id);
        },
        icon: Unarchive,
        label: 'Unarchive Group',
      },
    ];
    const removeGroupOption: DropdownOptionType = {
      value: 'removeGroup',
      onOptionClick: removeGroup,
      customColor: 'red',
      label: 'Remove Group',
      icon: Delete,
      'data-test-id': REMOVE_GROUP_ACTION_BTN_TEST,
    } as DropdownOptionType;

    if (isImCeltics) {
      archivedGroupDropdownOption.push(removeGroupOption);
      activeGroupDropdownOptions.push(removeGroupOption);
    }

    return (
      <Dropdown
        name="More"
        options={
          groupRequestParams.filter === GroupFilter.ARCHIVED ? archivedGroupDropdownOption : activeGroupDropdownOptions
        }
        optionsAlignment={OptionsAlignmentTypes.BOTTOM_LEFT}
        optionsWidth="20rem"
        closedElement={
          <DropdownButtonStyled $isOpened={false} color="primary" type="text" rightIconProp={<ChevronDown />}>
            MORE
          </DropdownButtonStyled>
        }
        openedElement={
          <DropdownButtonStyled color="primary" $isOpened rightIconProp={<ChevronDown />}>
            More
          </DropdownButtonStyled>
        }
        data-test-id={DASHBOARD_ACTIONS_DROPDOWN_TEST}
      />
    );
  },
);

export default MoreDropdown;
